/* eslint-disable react/button-has-type */
import React, {SyntheticEvent} from 'react';
import classNames from 'classnames';

import {Loader} from 'components/__helpers/loader/loader';

import s from './button.scss';

export interface PropsType {
    /**
     * @ignore
     */
    children: React.ReactNode;
    /**
     * on click event handler
     * @param {SyntheticEvent} event - on click event
     */
    onClick?: (event: SyntheticEvent<HTMLButtonElement>) => void;
    /**
     * Size of button and font. Allowed sizes: tiny, small, medium, big, large.
     */
    size?: 'tiny' | 'small' | 'medium' | 'big' | 'large';
    /**
     * Color of button. Allowed colors: primary, secondary, dark, light, midnight.
     */
    color?: 'primary' | 'secondary' | 'dark' | 'light' | 'midnight';
    /**
     * Type of button.
     * @see [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type)
     */
    type?: 'button' | 'submit' | 'reset';
    /**
     * External classes.
     */
    className?: string;
    /**
     * If true button will be rendered without background and border.
     */
    transparent?: boolean;
    /**
     * If true button will be rendered without background.
     */
    bordered?: boolean;
    /**
     * Disable state
     */
    disabled?: boolean;
    /**
     * Show loader
     */
    loading?: boolean;
    /**
     * Fill size by parent element
     */
    full?: boolean;
    /**
     * Focus state
     */
    focused?: boolean;
    /**
     * The initial value of the button
     */
    value?: string;
    /**
     * Attribute for autotests.
     */
    dataTestName?: string | null;
    /**
     * Makes button round
     */
    round?: boolean;
}

export const LOADER_SIZES_MAP = {
    tiny: 55,
    small: 70,
    medium: 80,
    big: 90,
    large: 110,
};

/**
 * Button which must be used at whole of the GlassesUsa
 * @param {PropsType} props - Props of component
 * @returns {React.ReactNode} Rendered component
 */
export const Button = React.memo<PropsType>(
    ({
        size = 'medium',
        color = 'primary',
        onClick = () => {},
        children,
        disabled = false,
        transparent = false,
        bordered = false,
        loading = false,
        full = false,
        className = '',
        type = 'button',
        focused = false,
        value = '',
        dataTestName = null,
        round = false,
    }: PropsType) => {
        const colorClassName = s[`color-${color}`];
        const sizeClassName = s[`size-${size}`];
        const isDisabled = disabled || loading;

        return (
            <button
                disabled={isDisabled}
                onClick={onClick}
                type={type}
                value={value}
                className={classNames(s.button, colorClassName, sizeClassName, className, {
                    [s.disabled]: disabled,
                    [s.loading]: loading,
                    [s.transparent]: transparent,
                    [s.bordered]: bordered,
                    [s.full]: full,
                    [s.focused]: focused,
                    [s.round]: round,
                })}
                data-test-name={dataTestName}
            >
                <span className={s.children}>{children}</span>
                {loading && (
                    <Loader className={s.loader} size={LOADER_SIZES_MAP[size]} color="white" />
                )}
            </button>
        );
    },
);
